/**
 * 高级数据表格组件
 * 基于 @tanstack/react-table 和 @you/ui 的基础 Table 组件
 * 支持排序、筛选、分页、行选择等功能
 */
import * as React from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
  PaginationState,
} from '@tanstack/react-table'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@you/ui'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  /** 列宽配置，key为列ID，value为宽度（支持px、%、auto等CSS宽度值） */
  columnWidths?: Record<string, string>
  /** 是否固定表头 */
  stickyHeader?: boolean
  /** 固定表头时的最大高度 */
  maxHeight?: number | string
  /** 是否带有纵向边框 */
  border?: boolean
  /** 是否为斑马纹表格 */
  stripe?: boolean
  /** 分页状态 */
  pagination?: PaginationState
  /** 分页状态变化回调 */
  onPaginationChange?: (pagination: PaginationState) => void
  /** 是否启用分页 */
  enablePagination?: boolean
  /** 是否透明背景 */
  transparent?: boolean
}

/**
 * 数据表格组件
 * @param columns 列定义
 * @param data 表格数据
 */
export function DataTable<TData, TValue>({
  columns,
  data,
  columnWidths,
  stickyHeader,
  maxHeight,
  border,
  stripe,
  pagination,
  onPaginationChange,
  enablePagination = false,
  transparent = false,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})

  // 内部分页状态，当外部没有提供分页状态时使用
  const [internalPagination, setInternalPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    })

  // 使用外部分页状态或内部分页状态
  const currentPagination = pagination || internalPagination
  const setCurrentPagination = (updater: any) => {
    const newPagination =
      typeof updater === 'function' ? updater(currentPagination) : updater
    if (onPaginationChange) {
      onPaginationChange(newPagination)
    } else {
      setInternalPagination(newPagination)
    }
  }

  React.useEffect(() => {
    console.log('data', data)
  }, [data])

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    // 分页配置
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined,
    onPaginationChange: setCurrentPagination,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: enablePagination ? currentPagination : undefined,
    },
    // 手动分页配置
    manualPagination: enablePagination && !!pagination,
    pageCount:
      enablePagination && !!pagination
        ? Math.ceil(data.length / (pagination.pageSize || 10))
        : undefined,
  })

  return (
    <div className="w-full">
      <Table
        border={border}
        stripe={stripe}
        stickyHeader={stickyHeader}
        maxHeight={maxHeight}
        className="rounded border"
        style={{
          borderRadius: '4px',
          border: '1px solid #EFF3FD',
          backgroundColor: transparent ? 'transparent' : undefined,
        }}
      >
        <TableHeader
          sticky={stickyHeader}
          style={{
            background: transparent ? 'transparent' : '#F7F9FF',
          }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow className="border-b-[#EFF3FD]" key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                // 根据列ID设置特定宽度
                const width = columnWidths?.[header.column.id] || 'auto'

                return (
                  <TableHead
                    key={header.id}
                    className="text-center"
                    style={{ width }}
                  >
                    {header.isPlaceholder ? null : (
                      <span>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </span>
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={
                      cell.column.id === 'userName'
                        ? 'text-left'
                        : 'text-center'
                    }
                  >
                    <span>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </span>
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                暂无数据
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
